import { useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { Loader2 } from "lucide-react";

import { cn, getText } from "shared/lib";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "shared/components";
import { EditorToolbar } from "./EditorToolbar";

export interface EditProposalDialogProps {
  content: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (content: string) => void;
  submitting?: boolean;
}

export function EditProposalDialog({
  content,
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: EditProposalDialogProps) {
  const [isEditable, setIsEditable] = useState(true);

  const extensions = useMemo(() => {
    return [
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            class: "text-bodylg font-semibold",
          },
          levels: [1],
        },
        paragraph: {
          HTMLAttributes: {
            class: "text-bodylg",
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: "list-disc ml-6",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal ml-6",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "marker:text-bodysm",
          },
        },
      }),
      Markdown.configure({
        tightLists: true,
        breaks: true,
      }),
    ];
  }, []);

  const editor = useEditor({
    content,
    extensions,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-hidden",
          !isEditable && "text-textSubdued bg-interfaceBgLight",
        ),
      },
      editable: () => isEditable,
    },
    onUpdate({ editor }) {
      setIsEditable(editor.isEditable);
    },
  });

  useEffect(() => {
    if (!editor) return;

    editor.commands.setContent(content);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsEditable(!submitting);
  }, [submitting]);

  const handleClickSubmitChanges = () => {
    if (editor) {
      onSubmit(editor.storage.markdown.getMarkdown());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[80%] flex-col gap-6 p-6 md:gap-8 md:p-8"
      >
        <div>
          <DialogTitle>{getText("Adjust your proposal")}</DialogTitle>

          <p className="text-bodymd text-textPrimary mt-1">
            {getText(
              "Adjusting your proposal will re-generate your permit and application requirements, which will take about 2 minutes.",
            )}
          </p>
        </div>

        <div
          className={cn(
            "border-interactiveBorder focus-within:ring-ring flex flex-col overflow-hidden rounded border focus-within:ring-2 focus-within:ring-offset-2",
            !isEditable && "bg-interfaceBgLight",
          )}
        >
          <EditorToolbar disabled={!isEditable} editor={editor} />

          <div className="flex grow flex-col overflow-hidden">
            <div className="h-full grow overflow-y-auto px-5 py-4">
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            {getText("Cancel")}
          </Button>

          <Button onClick={handleClickSubmitChanges}>
            {submitting ? <Loader2 className="mr-2 animate-spin" /> : null}
            {getText("Submit changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditProposalDialog;
